import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import FileSaver from 'file-saver';
import * as moment from 'moment';

import { ResultSetWithScores } from '../../../services/resultSet/types';
import { TestConfig } from '../../../services/test-kit/types';
import { Router } from '@angular/router';
import { IRangeData } from '../../../services/score/types';
import { ScoreService } from '../../../services/score/score.service';
import { ApiService } from '../../../services/api.service';
import { config } from 'src/app/config';
import { ResultSetService } from '../../../services/resultSet/resultSet.service';
import { RoleService } from '../../../services/user/role.service';

interface Score {
  availableAnalytes: string[];
  analyteMapping: AnalyteMapping;
  rangeData: IRangeData;
  score: number;
}

interface AnalyteMapping {
  [key: string]: string;
}

interface IAnalyteReferenceData {
  referenceUnit: { [key: string]: string };
  referenceRange: { [key: string]: string };
}

interface IChartData {
  chartId: string;
  isHorizontal: boolean;
  score: Score;
  data: {
    analytes: string[];
  };
  labels: string[];
  values: number[];
  previousValues: number[];
  lows: number[];
  highs: number[];
  allRanges: IAnalyteReferenceData;
}

interface IReportOption {
  type: string;
  icon: string;
  action: string;
}

@Component({
  selector: 'app-analytes-graph',
  templateUrl: './analytes-graph.component.html',
  styleUrls: ['./analytes-graph.component.css'],
})
export class AnalytesGraphComponent implements OnInit, OnChanges {
  @Input() selectedResultSet: ResultSetWithScores;
  @Input() previousResultSet: ResultSetWithScores;
  @Input() selectedTestType: TestConfig;
  @Input() testTypes: TestConfig[];
  @Input() isLoading: boolean;
  @Input() isExpanded: boolean;

  @Output() onTestTypeSelect = new EventEmitter<TestConfig>();
  @Output() onExpand = new EventEmitter<boolean>();

  chartData: IChartData;
  reportOptions: IReportOption[] = [];

  constructor(
    private readonly router: Router,
    private apiService: ApiService,
    private readonly scoreService: ScoreService,
    private readonly resultSetService: ResultSetService,
    private readonly roleService: RoleService
  ) {}

  ngOnInit(): void {
    if (this.selectedResultSet) {
      const newAdaptiveScores = this.selectedResultSet.adaptiveScores.sort(
        (a, b) =>
          config.analyteSequenceArray.indexOf(a.analyteKey) -
          config.analyteSequenceArray.indexOf(b.analyteKey)
      );

      const newAdaptiveScoresPreviousResult =
        this.previousResultSet?.adaptiveScores.sort(
          (a, b) =>
            config.analyteSequenceArray.indexOf(a.analyteKey) -
            config.analyteSequenceArray.indexOf(b.analyteKey)
        );

      this.selectedResultSet.adaptiveScores = JSON.parse(
        JSON.stringify(newAdaptiveScores)
      );

      if (newAdaptiveScoresPreviousResult) {
        this.previousResultSet.adaptiveScores = JSON.parse(
          JSON.stringify(newAdaptiveScoresPreviousResult)
        );
      }

      this.setChartData();
    }

    this.initReportOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedResultSet?.currentValue) {
      const newAdaptiveScores = this.selectedResultSet.adaptiveScores.sort(
        (a, b) =>
          config.analyteSequenceArray.indexOf(a.analyteKey) -
          config.analyteSequenceArray.indexOf(b.analyteKey)
      );
      this.selectedResultSet.adaptiveScores = JSON.parse(
        JSON.stringify(newAdaptiveScores)
      );
      this.setChartData();
    }
  }

  initReportOptions() {
    if (this.roleService.isProvider) {
      this.reportOptions.push({
        type: 'Questionnaires',
        icon: 'help_center',
        action: "navigateToReportPage('create-questionnaire')",
      });
    }
    this.reportOptions.push(
      ...[
        {
          type: 'Algocyte Report',
          icon: 'picture_as_pdf',
          action: 'generateNewReport()',
        },
        {
          type: 'Individual Report',
          icon: 'insert_chart_outlined',
          action: "navigateToReportPage('individual')",
        },
        {
          type: 'Trends Graph',
          icon: 'insert_chart',
          action: "navigateToReportPage('graph')",
        },
        {
          type: 'Tabular Report',
          icon: 'article',
          action: "navigateToReportPage('table')",
        },
        {
          type: 'Folding Plot',
          icon: 'insights',
          action: "navigateToReportPage('folding-plots')",
        },
      ]
    );
  }

  performAction(action: string) {
    const newAction = `this.${action}`;
    eval(newAction);
  }

  setChartData() {
    const rangeData = this.scoreService.calculateRangeData(
      this.selectedResultSet.adaptiveScores,
      this.selectedResultSet.results.analyteResults
    );

    const labResult = this.selectedResultSet['labResult'];
    const observations = labResult['observations'];

    const referenceRange = observations?.length
      ? observations.reduce(
          (result, { observationIdentifier, referenceRange }) => ({
            ...result,
            [observationIdentifier]: referenceRange,
          }),
          {}
        )
      : this.selectedResultSet.results.analyteResults.reduce(
          (result, { analyteKey, referenceRange }) => ({
            ...result,
            [analyteKey]: referenceRange,
          }),
          {}
        );

    const analyteKeys = this.resultSetService.getAnalyteKeysFromSet(
      this.selectedResultSet
    );
    const units = this.resultSetService.getUnitsFromSet(this.selectedResultSet);
    const labelsMap = this.resultSetService.getLabelsFromSet(
      this.selectedResultSet
    );
    const labels = Object.keys(labelsMap);
    this.chartData = {
      chartId: 'barChartContainer',
      isHorizontal: false,
      data: {
        analytes: analyteKeys,
      },
      score: {
        availableAnalytes: labels,
        analyteMapping: labelsMap,
        rangeData,
        score: this.selectedResultSet.nonAdaptiveScore,
      },
      labels,
      values: labels.map((label) => {
        const score = this.selectedResultSet.adaptiveScores.find(
          (x) => x.analyteKey === label
        );
        return score ? score.value : null;
      }),
      previousValues: this.previousResultSet?.adaptiveScores.map(
        (x) => x.value
      ),
      lows: this.selectedResultSet.adaptiveScores.map(
        (x) => x.personalRangeMin
      ),
      highs: this.selectedResultSet.adaptiveScores.map(
        (x) => x.personalRangeMax
      ),
      allRanges: {
        referenceRange,
        referenceUnit: units,
      },
    };
  }

  sortArrayBySequence(sequence: string[], arrayToSort: string[]): string[] {
    const sequenceMap = new Map<string, number>();
    sequence.forEach((item, index) => sequenceMap.set(item, index));

    return arrayToSort.sort((a, b) => {
      const indexA = sequenceMap.get(a);
      const indexB = sequenceMap.get(b);

      return (
        (indexA !== undefined ? indexA : Number.MAX_SAFE_INTEGER) -
        (indexB !== undefined ? indexB : Number.MAX_SAFE_INTEGER)
      );
    });
  }

  getTitle(resultSet: ResultSetWithScores): string {
    const formattedDate = moment
      .utc(resultSet?.results?.observationDate)
      .format('DD MMM YYYY hh:mm A');

    return `${formattedDate} | ${resultSet.results.source} | ${resultSet.testType}`;
  }

  handleTabChange(testType: TestConfig) {
    this.onTestTypeSelect.emit(testType);
  }

  toggleExpanded() {
    this.onExpand.emit(!this.isExpanded);
  }

  navigateToReportPage(path: string) {
    const testType = this.selectedTestType?.name;

    switch (path) {
      case 'create-questionnaire':
        this.router.navigate([
          `/create-questionnaire/${this.selectedResultSet.id}`,
        ]);
        break;

      case 'graph':
        this.router.navigate(['/new-report/' + path + '/' + testType]);
        break;

      case 'table':
        this.router.navigate(['/new-report/' + path + '/' + testType]);
        break;

      case 'individual':
        this.router.navigate(['/new-report/' + path + '/' + testType]);
        break;

      case 'folding-plots':
        this.router.navigate(['/' + path]);
        break;

      case 'events':
        this.router.navigate(['/' + path]);
        break;

      default:
        this.router.navigate(['/' + path]);
        break;
    }
  }

  async generateNewReport() {
    if (this.selectedResultSet) {
      const data = await this.apiService
        .generateResultSetReport(this.selectedResultSet.id)
        .toPromise()
        .then((response: any) => {
          const blob = new Blob([response], {
            type: 'application/pdf',
          });

          FileSaver.saveAs(blob, 'algocyte_report.pdf');
        });
    }
  }

  // _openSnackBar(message) {
  //   this.snackBar.open(message, '', {
  //     panelClass: ['algocyte-snackbar'],
  //     horizontalPosition: 'end',
  //     verticalPosition: 'top',
  //     duration: 3000,
  //   });
  //   $('snack-bar-container').css({
  //     'min-width': 'fit-content',
  //     width: 'fit-content',
  //     'border-radius': '10px',
  //     'margin-top': '50px',
  //     'background-color': '#353a40',
  //     color: '#f7fbff',
  //     padding: '3px 16px',
  //   });
  // }
}
